import React, { useState } from 'react';
import { Workflow } from '../../../types/workflow';

type SubTab = 'Timeline' | 'Logs' | 'Metrics' | 'Incidents';

const LEVEL_COLOR: Record<string, string> = {
  info:  '#0F6E56',
  warn:  '#92400e',
  error: '#b91c1c',
};

function Sparkline({ values, color = '#063F5A' }: { values: number[]; color?: string }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  const w = 240;
  const h = 48;
  const step = w / (values.length - 1 || 1);
  const points = values.map((v, i) => `${i * step},${h - (v / max) * (h - 4) - 2}`).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
}

function Bars({ values, color }: { values: number[]; color: string }) {
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {values.map((v, i) => (
        <div key={i} className="w-2 rounded-t" style={{ height: `${(v / max) * 100}%`, background: color }} />
      ))}
    </div>
  );
}

export function ObservabilityTab({ workflow }: { workflow: Workflow }) {
  const [sub, setSub] = useState<SubTab>('Timeline');
  const o = workflow.observability;

  return (
    <div>
      <div className="flex gap-1 mb-4">
        {(['Timeline', 'Logs', 'Metrics', 'Incidents'] as SubTab[]).map(s => (
          <button
            key={s}
            onClick={() => setSub(s)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            style={{
              background: sub === s ? '#063F5A' : '#F5F7FA',
              color: sub === s ? 'white' : '#595A60',
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {sub === 'Timeline' && (
        <section className="bg-white border border-trim rounded-xl p-5">
          <h3 className="text-sm font-semibold text-ink mb-3">Execution timeline</h3>
          <ul className="space-y-2">
            {o.timeline.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="text-xs font-mono text-muted w-44 flex-shrink-0">{new Date(t.ts).toLocaleString()}</span>
                <span className="text-ink">{t.event}</span>
                {t.node_id && <span className="ml-auto text-xs font-mono text-muted">{t.node_id}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {sub === 'Logs' && (
        <section className="bg-white border border-trim rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-trim bg-wash">
                <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Time</th>
                <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Level</th>
                <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Node</th>
                <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Message</th>
              </tr>
            </thead>
            <tbody>
              {o.logs.map((l, i) => (
                <tr key={i} className="border-b border-wash last:border-0">
                  <td className="px-5 py-2 text-xs font-mono text-muted">{new Date(l.ts).toLocaleString()}</td>
                  <td className="px-5 py-2 text-xs font-mono font-medium" style={{ color: LEVEL_COLOR[l.level] }}>{l.level.toUpperCase()}</td>
                  <td className="px-5 py-2 text-xs font-mono text-copy">{l.node_id}</td>
                  <td className="px-5 py-2 text-sm text-ink">{l.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {sub === 'Metrics' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="bg-white border border-trim rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-2">Throughput / min (last 12)</h3>
            <Bars values={o.metrics.throughput_per_min} color="#14b8a6" />
          </section>
          <section className="bg-white border border-trim rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-2">Latency p50 (ms)</h3>
            <Sparkline values={o.metrics.latency_p50_ms} color="#063F5A" />
          </section>
          <section className="bg-white border border-trim rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-2">Latency p95 (ms)</h3>
            <Sparkline values={o.metrics.latency_p95_ms} color="#b91c1c" />
          </section>
          <section className="bg-white border border-trim rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-3">24h totals</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-wash rounded p-2.5"><div className="text-muted">Success rate</div><div className="text-ink font-mono mt-0.5">{o.metrics.success_rate_pct.toFixed(1)}%</div></div>
              <div className="bg-wash rounded p-2.5"><div className="text-muted">Failures</div><div className="text-ink font-mono mt-0.5">{o.metrics.failure_count_24h}</div></div>
              <div className="bg-wash rounded p-2.5"><div className="text-muted">API errors</div><div className="text-ink font-mono mt-0.5">{o.metrics.api_errors_24h}</div></div>
              <div className="bg-wash rounded p-2.5"><div className="text-muted">Tokens</div><div className="text-ink font-mono mt-0.5">{o.metrics.token_usage_24h.toLocaleString()}</div></div>
              <div className="bg-wash rounded p-2.5"><div className="text-muted">Infra cost</div><div className="text-ink font-mono mt-0.5">${o.metrics.infra_cost_24h_usd.toFixed(2)}</div></div>
              <div className="bg-wash rounded p-2.5"><div className="text-muted">Human review</div><div className="text-ink font-mono mt-0.5">{(o.metrics.human_review_rate * 100).toFixed(0)}%</div></div>
            </div>
          </section>
        </div>
      )}

      {sub === 'Incidents' && (
        <section className="bg-white border border-trim rounded-xl p-5">
          <h3 className="text-sm font-semibold text-ink mb-3">Incidents</h3>
          {workflow.recent_incidents.length === 0 ? (
            <div className="text-sm text-muted">No incidents recorded.</div>
          ) : (
            <ul className="space-y-2.5">
              {workflow.recent_incidents.map(i => (
                <li key={i.id} className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted">{i.id}</span>
                    <span className="text-ink font-medium">{i.title}</span>
                    <span className="ml-auto text-xs text-muted">{i.severity} · {i.status}</span>
                  </div>
                  <div className="text-xs text-muted mt-0.5">{new Date(i.opened_at).toLocaleString()} — {i.summary}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
